import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

import { Button } from './Button';
import { Dialog } from './Dialog';
import { Input } from './Input';

type PromptOpts = {
  title: string;
  description?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

type ConfirmOpts = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render the confirm button as a destructive action (red). */
  destructive?: boolean;
};

type DialogContextValue = {
  prompt: (opts: PromptOpts) => Promise<string | null>;
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
};

const DialogContext = createContext<DialogContextValue | null>(null);

/**
 * Promise-returning replacements for window.prompt() and window.confirm().
 * The DialogProvider renders one shared modal at the app root; useDialog()
 * gives any descendant an imperative API:
 *
 *   const { prompt, confirm } = useDialog();
 *   const name = await prompt({ title: 'Template name', defaultValue: 'Untitled' });
 *   if (!name) return;
 *   if (!await confirm({ title: 'Delete?', destructive: true })) return;
 *
 * Only one dialog can be open at a time — that matches native window.prompt
 * semantics and avoids stacking confusion. Subsequent calls during an open
 * dialog reject the previous promise with null/false.
 */
type State =
  | { kind: 'idle' }
  | {
      kind: 'prompt';
      opts: PromptOpts;
      value: string;
      resolve: (v: string | null) => void;
    }
  | { kind: 'confirm'; opts: ConfirmOpts; resolve: (v: boolean) => void };

export function DialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ kind: 'idle' });

  const close = useCallback(() => {
    if (state.kind === 'prompt') state.resolve(null);
    if (state.kind === 'confirm') state.resolve(false);
    setState({ kind: 'idle' });
  }, [state]);

  const prompt = useCallback(
    (opts: PromptOpts) =>
      new Promise<string | null>((resolve) => {
        setState((prev) => {
          if (prev.kind === 'prompt') prev.resolve(null);
          if (prev.kind === 'confirm') prev.resolve(false);
          return { kind: 'prompt', opts, value: opts.defaultValue ?? '', resolve };
        });
      }),
    [],
  );

  const confirm = useCallback(
    (opts: ConfirmOpts) =>
      new Promise<boolean>((resolve) => {
        setState((prev) => {
          if (prev.kind === 'prompt') prev.resolve(null);
          if (prev.kind === 'confirm') prev.resolve(false);
          return { kind: 'confirm', opts, resolve };
        });
      }),
    [],
  );

  return (
    <DialogContext.Provider value={{ prompt, confirm }}>
      {children}
      <Dialog
        open={state.kind !== 'idle'}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title={state.kind === 'idle' ? undefined : state.opts.title}
        description={state.kind === 'idle' ? undefined : state.opts.description}
        width={state.kind === 'prompt' ? 440 : 400}
      >
        {state.kind === 'prompt' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = state.value.trim();
              state.resolve(trimmed.length > 0 ? trimmed : null);
              setState({ kind: 'idle' });
            }}
          >
            <Input
              autoFocus
              value={state.value}
              placeholder={state.opts.placeholder}
              onChange={(e) =>
                setState((prev) =>
                  prev.kind === 'prompt' ? { ...prev, value: e.target.value } : prev,
                )
              }
              style={{ width: '100%', height: 38, marginBottom: 16 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button type="button" onClick={close}>
                {state.opts.cancelLabel ?? 'Cancel'}
              </Button>
              <Button type="submit" variant="flame">
                {state.opts.confirmLabel ?? 'OK'}
              </Button>
            </div>
          </form>
        )}
        {state.kind === 'confirm' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button onClick={close}>{state.opts.cancelLabel ?? 'Cancel'}</Button>
            <Button
              variant="flame"
              onClick={() => {
                state.resolve(true);
                setState({ kind: 'idle' });
              }}
              style={
                state.opts.destructive
                  ? { background: 'var(--err)', borderColor: 'var(--err)' }
                  : undefined
              }
            >
              {state.opts.confirmLabel ?? (state.opts.destructive ? 'Delete' : 'Confirm')}
            </Button>
          </div>
        )}
      </Dialog>
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useDialog must be used inside a <DialogProvider>');
  }
  return ctx;
}
